import setDynamicFormFieldValue from "@bevco/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@bevco/Store/Reducers";
import { colorStrings } from "@bevco/Utilities/variables";
import {
    DynamicFormOptionModel,
    getDynamicFormOptionDisplay,
    resolveDynamicFormOptions,
} from "@bevco/Utilities/dynamicFormOptions";
import { DynamicFormContext } from "@bevco/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@bevco/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@bevco/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import siteMessage from "@insite/client-framework/SiteMessage";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    fieldKey = "fieldKey",
    label = "label",
    options = "options",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.label]: string;
        [fields.options]: DynamicFormOptionModel[];
        [fields.isRequired]: boolean;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "";
    return {
        fieldKey,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

const checkboxStyles = {
    mergeCss: true,
    css: css`
        ${IconWrapper} {
            &[role="checkbox"] {
                &[aria-checked="true"],
                &[aria-checked="mixed"] {
                    & + label {
                        color: ${getColor(colorStrings.textMain)};
                        font-weight: 400;
                    }
                }
            }
        }
    `,
};

/** Splits a semicolon-delimited checkbox group value into selected options. */
const parseSelected = (value?: string) =>
    (value || "")
        .split(";")
        .map(item => item.trim())
        .filter(Boolean);

/** Salesforce Dynamic Form checkbox group widget. */
const DynamicCheckboxGroup: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    fieldValue,
    setDynamicFormFieldValue,
}) => {
    const { label, isRequired, options } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    const resolvedOptions = React.useMemo(() => resolveDynamicFormOptions(options), [options]);
    const selected = parseSelected(fieldValue);
    useRegisterDynamicFormFieldKeys(id, fieldKey ? [fieldKey] : []);

    const validate = React.useCallback(() => {
        const error =
            isRequired && selected.length === 0 ? (siteMessage("Field_Required", label) as string) : "";
        setErrorMessage(error);
        return !error;
    }, [isRequired, label, selected.length]);

    React.useEffect(() => {
        if (!fieldKey) {
            return undefined;
        }
        formContext.validators[fieldKey] = validate;
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, validate, formContext.validators]);

    React.useEffect(() => {
        if (errorMessage && selected.length > 0) {
            setErrorMessage("");
        }
    }, [selected.length, errorMessage]);

    const toggleOption = (value: string, checked: boolean) => {
        const next = checked
            ? [...selected.filter(item => item !== value), value]
            : selected.filter(item => item !== value);
        setDynamicFormFieldValue({ key: fieldKey, value: next.join(";") });
    };

    if (!fieldKey) {
        return null;
    }

    return (
        <DynamicFormFieldShell>
            <CheckboxGroup label={label} required={isRequired} error={errorMessage || undefined}>
                {resolvedOptions.map(option => (
                    <Checkbox
                        key={option.value}
                        {...checkboxStyles}
                        checked={selected.indexOf(option.value) >= 0}
                        onChange={(_, checked) => toggleOption(option.value, checked)}
                    >
                        {option.label}
                    </Checkbox>
                ))}
            </CheckboxGroup>
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicCheckboxGroup),
    definition: {
        group: widgetGroup,
        displayName: "Checkbox Group",
        icon: "list-check",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.options,
                displayName: "Options",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => getDynamicFormOptionDisplay(item),
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "label",
                        displayName: "Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "value",
                        displayName: "Value (sent to Salesforce)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        tooltip: "API value submitted to Salesforce (can differ from the Label).",
                    },
                ],
                defaultValue: [],
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
